<template>
    <!-- Bare wrapper - Vue 2 needs a single template root, and the live-preview portal below must
         render as a sibling of the popup's own root (active exactly when it isn't: see
         backgroundExport on both). -->
    <div>
    <!-- Once an export is running, the popup closes from the user's point of view (see finish())
         but this component instance stays alive and keeps exporting in the background until it
         resolves - v-if right here just stops rendering its own (now-stale, no longer actionable)
         UI, it doesn't unmount the component itself the way a *parent*-side v-if on <MediaEditor>
         would. That's deliberate: a failure used to leave the editor permanently stuck open with
         nothing to do about it, and staying open during a real export made it look like further
         edits still had an effect, when the timeline being encoded was already a frozen snapshot. -->
    <div
        class="containerMediaEditor"
        v-if="!backgroundExport"
        @mousedown="onBackdropMouseDown"
        @click="onBackdropClick"
    >
        <div
            class="mediaEditor"
            :class="{ 've-drop-active': isDraggingFileOver }"
            @dragenter.prevent.stop="onDragEnter"
            @dragover.prevent.stop
            @dragleave.prevent.stop="onDragLeave"
            @drop.prevent.stop="onFilesDropped"
        >
            <div class="ve-drop-overlay" v-if="isDraggingFileOver">
                <i class="fa-solid fa-file-import"></i>
                <p>{{ $t('dash.post-creator.media-editor.editor.drop-media-hint') }}</p>
            </div>
            <div class="header-media-editor">
                <div class="btn-close" @click="requestClose">
                    <i class="fa-solid fa-xmark"></i>
                </div>
                <p class="ve-editor-title">
                    {{ $t(isPhotoMode ? 'dash.post-creator.media-editor.editor.title-photo' : 'dash.post-creator.media-editor.editor.title') }}
                    <span class="ve-beta-badge">({{ $t('dash.post-creator.media-editor.editor.beta-badge') }})</span>
                </p>
                <div class="header-actions">
                    <div class="btn-history-group" v-if="timeline">
                        <Tooltip
                            :text="$t('dash.post-creator.media-editor.editor.undo')"
                            :classCustom="'btn-history' + (!canUndo ? ' disabled' : '')"
                            @click="undo"
                        ><i class="fa-solid fa-arrow-left"></i></Tooltip>
                        <Tooltip
                            :text="$t('dash.post-creator.media-editor.editor.redo')"
                            :classCustom="'btn-history' + (!canRedo ? ' disabled' : '')"
                            @click="redo"
                        ><i class="fa-solid fa-arrow-right"></i></Tooltip>
                    </div>
                    <div class="btn-revert" v-if="timeline" @click="requestRevert">
                        <i class="fa-solid fa-clock-rotate-left"></i> {{ $t('dash.post-creator.media-editor.editor.revert') }}
                    </div>
                    <div class="btn-mode-toggle" v-if="timeline" @click="toggleDocumentMode">
                        <i :class="isPhotoMode ? 'fa-solid fa-clapperboard' : 'fa-solid fa-image'"></i>
                        {{ $t(modeToggleLabelKey) }}
                    </div>

                    <div class="template-trigger-wrapper" v-if="timeline && templatesFeatureEnabled">
                        <TemplateTrigger :open="showMediaTemplatePicker" @open="action => { mediaTemplateAction = action; showMediaTemplatePicker = true }">
                            <div class="btn-mode-toggle">
                                <i class="fa-regular fa-swatchbook"></i>
                                {{ $t('dash.templates.media-templates') }}
                            </div>
                        </TemplateTrigger>
                        <TemplatePicker
                            v-if="showMediaTemplatePicker"
                            :action="mediaTemplateAction"
                            type="media"
                            :save-data="saveMediaTemplateData"
                            :save-unavailable-text="saveMediaTemplateData ? '' : $t('dash.templates.save-unavailable-media')"
                            @apply="applyMediaTemplate"
                            @saved="showMediaTemplatePicker = false"
                            @close="showMediaTemplatePicker = false"
                        />
                    </div>

                    <!-- No "exporting..." state to show here anymore - clicking this closes the
                         popup immediately and finishes the export in the background (see finish()
                         and the root v-if above), so this component is already hidden by the time
                         exporting actually becomes true. Progress instead shows on the media
                         thumbnail in the post form itself (see Form.vue's @export-progress). -->
                    <div class="btn-finish" :class="{ disabled: !timeline }" @click="finish">
                        {{ $t('dash.post-creator.media-editor.editor.finish') }}
                    </div>
                </div>
            </div>

            <div class="placement-backdrop" v-if="pendingOverlay" @click="pendingOverlay = null">
                <div class="placement-hint">{{ $t('dash.post-creator.media-editor.editor.placement-hint') }} <span class="pn-cancel">({{ $t('dash.post-creator.media-editor.editor.cancel') }})</span></div>
            </div>

            <template v-if="timeline">
                <div class="ve-main">
                    <OverlayPalette
                        :currentMs="currentMs"
                        :timelineDurationMs="durationMs"
                        :activeTool="activeClipTool"
                        :photoMode="isPhotoMode"
                        @start-placement="startPlacement"
                        @start-image-placement="onStartImagePlacement"
                        @select-clip-tool="onSelectClipTool"
                        @split-clip="splitSelectedClip"
                        @close-side-panel="activeClipTool = null"
                    />

                    <ElementsTree
                        v-if="isPhotoMode && overlayTrack && overlayTrack.overlays.length"
                        :overlays="overlayTrack ? overlayTrack.overlays : []"
                        :selectedOverlayId="selectedOverlayId"
                        :selectedGroupId="selectedGroupId"
                        @select="selectedOverlayId = $event"
                        @select-group="onSelectGroupFromTree"
                        @remove="removeOverlay"
                        @duplicate="onDuplicateOverlay"
                        @reorder="reorderOverlay"
                        @ungroup="onUngroupOverlays"
                        @duplicate-group="onDuplicateOverlayGroup"
                        @remove-group="onRemoveOverlayGroup"
                    />

                    <VideoPreviewCanvas
                        ref="preview"
                        :class="{ 'placement-active': !!pendingOverlay }"
                        :timeline="timeline"
                        :overlays="overlayTrack ? overlayTrack.overlays : []"
                        :resolveSource="resolveSource"
                        :placementMode="!!pendingOverlay"
                        :excludeOverlayId="pendingOverlay && pendingOverlay.repositionId ? pendingOverlay.repositionId : null"
                        :cropClip="cropSelectorClip"
                        @time-update="onTimeUpdate"
                        @playing-changed="playing = $event"
                        @update:overlay-transform="onOverlayTransformUpdate"
                        @place="onPlaceOverlay"
                        @select-overlay="selectedOverlayId = $event"
                        @update-crop-rect="onUpdateCropRect"
                        @crop-commit="commitCropRect"
                        @update-overlay-data="onUpdateOverlayData"
                        @media-error="onMediaError"
                        @duplicate-selection="onDuplicateOverlaySelection"
                        @remove-selection="onRemoveOverlaySelection"
                        @group-selection="onGroupOverlays"
                        @ungroup-selection="onUngroupOverlays"
                        @multi-selection-change="onMultiSelectionChange"
                    />

                    <!-- A media that fails to load (expired/broken URL, network error, unsupported
                         codec) previously left the preview *and* the timeline filmstrip silently
                         black forever, with no feedback at all - see TimelinePlayer.js's 'error'
                         listener. -->
                    <div class="ve-media-error-banner" v-if="mediaErrorFileIds.length">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        <span>{{ $t('dash.post-creator.media-editor.editor.media-error') }}</span>
                        <div class="ve-media-error-retry" @click="retryFailedMedia">{{ $t('dash.post-creator.media-editor.editor.media-error-retry') }}</div>
                    </div>

                    <OverlayConfigPanel
                        v-if="selectedOverlay"
                        ref="sidePanel"
                        :overlay="selectedOverlay"
                        :photoMode="isPhotoMode"
                        :timelineDurationMs="durationMs"
                        :canvasSize="{ width: timeline.canvas.width, height: timeline.canvas.height }"
                        @update-overlay="onUpdateOverlay"
                        @remove-overlay="removeOverlay"
                        @duplicate-overlay="onDuplicateOverlay"
                        @reposition="startReposition"
                        @reorder="reorderOverlay"
                        @close="closeOverlayConfig"
                    />
                    <MusicPanel
                        v-else-if="activeClipTool === 'music'"
                        ref="sidePanel"
                        :audioClips="audioTrack ? audioTrack.clips : []"
                        :fileNames="fileNames"
                        :sources="sources"
                        @add-track="addBackgroundAudio"
                        @remove-track="removeAudioClip"
                        @update-track="onUpdateAudioClip"
                        @close="activeClipTool = null"
                    />
                    <ClipToolPanel
                        v-else-if="activeClipTool"
                        ref="sidePanel"
                        :clip="selectedClip"
                        :tool="activeClipTool"
                        :canvasBackground="timeline.canvas"
                        :detectingSilence="detectingSilence"
                        :silenceDetectionResult="silenceDetectionResult"
                        :silenceThresholdRatio="silenceThresholdRatio"
                        :silenceMinDurationMs="silenceMinDurationMs"
                        :silencePreviewActive="!!(silencePreview && selectedClip && silencePreview.clipId === selectedClip.id)"
                        :networksSelected="networksSelected"
                        @update-crop-ratio="ratio => applyCropRatio({ clipId: selectedClipId, ratio })"
                        @update-crop-rect="onCropRectInput"
                        @update-fill-mode="mode => onUpdateFillMode({ clipId: selectedClipId, mode })"
                        @update-rotation="onClipToolRotation"
                        @update-speed="rate => onUpdateClip({ clipId: selectedClipId, patch: { playbackRate: rate } })"
                        @update-preserve-pitch="preserve => onUpdateClip({ clipId: selectedClipId, patch: { preservePitch: preserve } })"
                        @update-background="onUpdateBackground"
                        @update-transition="transitionOut => onUpdateClip({ clipId: selectedClipId, patch: { transitionOut } })"
                        @update-silence-settings="updateSilenceSettings"
                        @validate-silence="validateSilencePreview"
                        @reset-silence="resetSilencePreview"
                        @cancel-silence="cancelSilencePreview"
                        @update-audio="patch => onUpdateClip({ clipId: selectedClipId, patch })"
                        @close="activeClipTool = null"
                    />
                </div>

                <Timeline
                    v-if="!isPhotoMode"
                    ref="timeline"
                    :timeline="timeline"
                    :sources="sources"
                    :selectedClipId="selectedClipId"
                    :overlays="overlayTrack ? overlayTrack.overlays : []"
                    :selectedOverlayId="selectedOverlayId"
                    :audioClips="audioTrack ? audioTrack.clips : []"
                    :fileNames="fileNames"
                    :currentMs="currentMs"
                    :totalDurationMs="durationMs"
                    :compact="!!selectedOverlayId"
                    :expanded="timelineExpanded"
                    :playing="playing"
                    :activeClipTool="activeClipTool"
                    :silencePreview="silencePreview"
                    @select-clip="selectedClipId = $event"
                    @remove-clip="removeClip"
                    @add-clip="addClip"
                    @add-blank-image="addBlankImageClip"
                    @update-clip="onUpdateClip"
                    @toggle-silence-segment="toggleSilencePreviewSegment"
                    @move-silence-boundary="moveSilencePreviewBoundary"
                    @reorder-clips="onReorderClips"
                    @seek="onSeek"
                    @toggle-play="togglePlay"
                    @select-overlay="selectedOverlayId = $event"
                    @remove-overlay="removeOverlay"
                    @duplicate-overlay="onDuplicateOverlay"
                    @update-overlay="onUpdateOverlay"
                    @update-audio-clip="onUpdateAudioClip"
                    @remove-audio-clip="removeAudioClip"
                    @toggle-expand="timelineExpanded = !timelineExpanded"
                    @select-clip-tool="onSelectClipTool"
                    @split-clip="splitSelectedClip"
                    @toggle-fullscreen="toggleFullscreen"
                    @preview-seek="onPreviewSeek"
                />
            </template>
            <!-- Async mounted() probes the source media before timeline exists (see below) - this
                 filled the near-empty popup with just a small pink pill up top, styled the same as
                 an actual error notice, which read as something being wrong rather than a normal,
                 brief loading state. Centered/neutral/spinner instead makes the "still loading"
                 intent unambiguous at a glance. -->
            <div v-else class="ve-loading-notice">
                <i class="fa-solid fa-circle-notch ve-loading-spinner"></i>
                <p>{{ $t('dash.post-creator.media-editor.editor.loading') }}</p>
            </div>

            <div class="exit-confirm-backdrop" v-if="showExitConfirm">
                <div class="exit-confirm">
                    <p>{{ $t('dash.post-creator.media-editor.editor.unsaved-changes') }}</p>
                    <div class="exit-confirm-actions">
                        <div class="ec-btn ec-cancel" @click="showExitConfirm = false">{{ $t('dash.post-creator.media-editor.editor.cancel') }}</div>
                        <div class="ec-btn ec-discard" @click="confirmDiscard">{{ $t('dash.post-creator.media-editor.editor.discard') }}</div>
                        <div class="ec-btn ec-save" @click="confirmSave">{{ $t('dash.post-creator.media-editor.editor.save') }}</div>
                    </div>
                </div>
            </div>

            <div class="exit-confirm-backdrop" v-if="showRevertConfirm">
                <div class="exit-confirm">
                    <p>{{ $t('dash.post-creator.media-editor.editor.revert-confirm') }}</p>
                    <div class="exit-confirm-actions">
                        <div class="ec-btn ec-cancel" @click="showRevertConfirm = false">{{ $t('dash.post-creator.media-editor.editor.cancel') }}</div>
                        <div class="ec-btn ec-discard" @click="revertToOriginal">{{ $t('dash.post-creator.media-editor.editor.revert') }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Placeholder shown in the post preview while an export is running, portalled to whichever
         slot matches this session's own scope (see Phone.vue's matching portal-target) - a live
         rendered canvas was tried here first (real timeline/overlays playback) but proved
         unreliable (wrong per-network autoplay/mute behavior, missing/mispositioned overlays,
         choppy playback) - a static frame (see exportSnapshotUrl/finish()) + status message is
         simple and always correct instead. Named by scope (not a single shared target) so several
         simultaneous exports (general/page/network) never render stacked on top of each other -
         only the currently-viewed scope's own target is ever mounted on the consumer side. -->
    <portal :to="'media-preview-' + scopeKey" v-if="backgroundExport">
        <div class="live-preview-placeholder">
            <img v-if="exportSnapshotUrl" :src="exportSnapshotUrl" alt="">
            <img v-else-if="isPhotoMode" :src="videoSrc" alt="">
            <video v-else :src="videoSrc" muted playsinline preload="metadata"></video>
            <div class="live-preview-placeholder-status">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>{{ $t('dash.post-creator.media-editor.editor.generating') }} {{ Math.round(exportProgressPct * 100) }}%</span>
            </div>
        </div>
    </portal>
    </div>
</template>

<script>
import VideoPreviewCanvas from './components/VideoPreviewCanvas.vue'
import Timeline from './components/Timeline.vue'
import ElementsTree from './components/ElementsTree.vue'
import OverlayPalette from './components/OverlayPalette.vue'
import OverlayConfigPanel from './components/OverlayConfigPanel.vue'
import ClipToolPanel from './components/ClipToolPanel.vue'
import MusicPanel from './components/MusicPanel.vue'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import TemplatePicker from '@/components/dash/shared/TemplatePicker.vue'
import TemplateTrigger from '@/components/dash/shared/TemplateTrigger.vue'
import TemplatesFeatureFlag from '@/components/mixins/TemplatesFeatureFlag.vue'
import { deepClone } from '@/components/dash/postCreator/utils.js'
import {
    createEmptyTimeline,
    createVideoClip,
    createImageClip,
    createAudioClip,
    createOverlay,
    addClipToTrack,
    removeClipFromTrack,
    recomputeTimelineLayout,
    addOverlayToTrack,
    removeOverlayFromTrack,
    reorderOverlayInTrack,
    splitMediaClip,
    computeSegmentsFromSilenceRanges,
    computeCropRectForRatio,
    getMediaTrack,
    getOverlayTrack,
    getAudioTrack,
    computeTimelineDurationMs,
    cloneTimeline,
    duplicateOverlay,
    duplicateMediaClip,
    generateId
} from './engine/timelineModel.js'
import { analyzeAudioLoudness, extractSilenceRanges, DEFAULT_SILENCE_THRESHOLD_RATIO, DEFAULT_MIN_SILENCE_DURATION_MS } from './engine/silenceDetection/silenceDetector.js'
import { capResolution } from './engine/export/qualitySettings.js'
import { probeSourceMediaStats, chooseExportFps } from './engine/probeFrameRate.js'
import { CLIP_SOURCE_TYPES } from './engine/constants.js'

const ORIGIN_FILE_ID = 'origin'
// Undo/redo history - a snapshot per meaningful edit, not per raw event (a live drag fires
// markDirty on every tick) - the debounce coalesces a burst of changes into the single resulting
// state once it settles. Each snapshot is a deepClone of the timeline metadata only (positions,
// trims, overlay data...), never the actual media files (those stay in fileRegistry, referenced
// by fileId) - so it stays cheap even at a high cap. The cap itself is just a safety net against
// a pathological, unbounded edit loop, not a real memory concern.
const MAX_HISTORY = 200
const HISTORY_PUSH_DEBOUNCE_MS = 600

export default {
    name: 'MediaEditor',

    mixins: [TemplatesFeatureFlag],

    components: {
        VideoPreviewCanvas,
        Timeline,
        ElementsTree,
        OverlayPalette,
        OverlayConfigPanel,
        ClipToolPanel,
        MusicPanel,
        Tooltip,
        TemplateTrigger,
        TemplatePicker
    },

    props: {
        videoSrc: {
            type: String,
            required: true
        },
        localFile: {
            type: File,
            default: null
        },
        timelineState: {
            type: Object,
            default: null
        },
        // Video/image/audio clips added *on top of* the original video during a previous editing
        // session ({fileId: File}) - the original is passed via videoSrc/localFile and resolved
        // through Form.vue's own files[], but these extra clips only ever existed as in-memory
        // blobs inside the editor, so they must be handed back in here to restore a multi-clip
        // timeline (otherwise every fileId but the origin's is unresolvable and the whole
        // restore is silently discarded, reverting to a blank single-clip timeline).
        extraFiles: {
            type: Object,
            default: () => ({})
        },
        // Which networks (page.network keys) are actually selected for this post - passed straight
        // through from Form.vue's own prop of the same name. Used only to prioritize the crop
        // tool's per-network format sections (see ClipToolPanel.vue's networkRatioSections) -
        // nothing else in the editor depends on it.
        networksSelected: {
            type: Array,
            default: () => []
        },
        // 'video' (default, existing behavior unchanged) | 'image' - lets a session start from an
        // image as the origin/seed clip instead of a video (see Form.vue's convertImageToVideo,
        // the image editor's own "Créer une vidéo" button). Drives both the origin's fileRegistry
        // kind and which clip factory mounted()/buildFreshTimeline() use for it.
        originSourceType: {
            type: String,
            default: CLIP_SOURCE_TYPES.VIDEO
        },
        // A gif is animated - even though it's a still-image file (originSourceType stays
        // 'image', it still has to seed an image clip, not a video element), it defaults to
        // documentMode 'video' below rather than 'photo' like any other image.
        originIsGif: {
            type: Boolean,
            default: false
        },
        // 'photo' | 'video' | null (default). originSourceType always reflects the deep original
        // source, which never changes even after a prior edit round converted the saved output to
        // the other mode - startMode (StandardTab.vue passes the *currently saved* file's type)
        // overrides documentMode's origin-based default so re-editing reopens in the right mode.
        startMode: {
            type: String,
            default: null
        },
        // Identifies which page/network/general slot this session belongs to (StandardTab.vue's
        // own scopeKey) - passed through to mediaExportJobs/startExport purely for display/
        // bookkeeping in the store, not used by this component itself.
        scopeKey: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            timeline: null,
            fileRegistry: {},
            showMediaTemplatePicker: false,
            mediaTemplateAction: 'apply',
            selectedClipId: null,
            selectedOverlayId: null,
            // Mirrors VideoPreviewCanvas's own multi-selection state (see its multi-selection-change
            // emit) - selectedOverlayId stays null the whole time a group/ad-hoc multi-selection is
            // active on the canvas, same as "nothing selected", so hasOpenSidePanel needs this too.
            hasMultiSelection: false,
            // groupId only when the current multi-selection is a *complete* group - drives
            // ElementsTree.vue's own highlight, a second readout of the same state independent of
            // whatever the canvas's own frame is actually rendering.
            selectedGroupId: null,
            currentMs: 0,
            // See onBackdropMouseDown/onBackdropClick - only a mousedown that begins directly on
            // the backdrop itself arms the next click to actually close the editor.
            backdropMouseDownOnly: false,
            // Re-entrancy guard only now (see finish()) - errors are emitted straight up to
            // Form.vue (export-failed) rather than kept as local state to render, since this
            // component's own UI is already hidden by the time that matters.
            exporting: false,
            // See finish() and the root v-if in the template - true from the moment "Terminer" is
            // clicked until the export settles (success or failure), hiding this component's own
            // UI while it keeps running in the background instead of blocking on it.
            backgroundExport: false,
            // Live-preview-in-progress placeholder (see the portal below) - a one-off still
            // captured from the popup's own canvas right before it's hidden (see finish()), so the
            // export-in-progress thumbnail actually reflects any crop/overlays/current frame
            // already applied instead of just the raw origin media. Progress is duplicated here
            // (also emitted as export-progress) purely to render the percentage in that same
            // placeholder - the emit alone was enough before there was any local UI depending on it.
            exportSnapshotUrl: null,
            exportProgressPct: 0,
            detectingSilence: false,
            silenceDetectionResult: null,
            // fileIds whose media element failed to load (see TimelinePlayer.js's 'error' listener/
            // onMediaError) - drives the media-error banner below, since a silently-black preview
            // with no feedback was impossible to tell apart from "still loading".
            mediaErrorFileIds: [],
            // Ctrl+C/Ctrl+V - holds a deep-cloned snapshot (not a live reference) of whichever
            // overlay/clip was selected at copy time, so deleting or further editing the original
            // afterwards can't corrupt what gets pasted.
            clipboard: null,
            // Drag-and-drop a new media file straight into the editor (appends at the end of the
            // main track). dragCounter (not a plain boolean) because dragenter/dragleave fire on
            // every child element the cursor crosses (header, preview, timeline...) as they bubble
            // up to this single listener - without it the overlay flickers on/off mid-drag.
            isDraggingFileOver: false,
            dragCounter: 0,
            // Undo/redo - a capped history of full timeline snapshots. history[historyIndex] is
            // always the one matching the currently-displayed state. isRestoringHistory guards
            // against undo/redo's own timeline swap being mistaken for a new edit worth recording.
            history: [],
            historyIndex: -1,
            isRestoringHistory: false,
            historyPushTimer: null,
            // Keyed by clip.id - the decoded loudness profile (see analyzeAudioLoudness), cached so
            // adjusting sensitivity/minimum duration afterwards can re-run detection instantly
            // instead of re-fetching and re-decoding the whole audio track on every slider tick.
            silenceAnalysisCache: {},
            silenceThresholdRatio: DEFAULT_SILENCE_THRESHOLD_RATIO,
            silenceMinDurationMs: DEFAULT_MIN_SILENCE_DURATION_MS,
            // { clipId, segments } while reviewing a detection result (or re-editing an already
            // applied one) - see initSilencePreview/validateSilencePreview. Nothing is actually
            // committed to the clip (and nothing else on the timeline reflows) until validated -
            // the clip being reviewed temporarily shows its *full*, pre-cut length on the timeline
            // instead, with kept/cut zones marked within it (see TimelineClip.vue's own
            // previewSegments prop), so there's something concrete to look at and adjust before
            // deciding.
            silencePreview: null,
            pendingOverlay: null,
            isDirty: false,
            // state at open (fresh or restored re-edit), see hasChangesSinceOpen - kept apart from
            // history[0] since the capped history shifts it out after enough edits
            initialTimelineJson: null,
            initialDocumentMode: null,
            showExitConfirm: false,
            showRevertConfirm: false,
            playing: false,
            activeClipTool: null,
            originProbe: null,
            timelineExpanded: true,
            // 'photo' | 'video' - a pure UI/export mode, not a data transformation: the underlying
            // clip/overlay model is identical either way, so toggling (see toggleDocumentMode)
            // never mutates timeline. startMode (see its own prop comment) wins outright when
            // given; otherwise defaults to 'photo' only for a genuine still image origin - a gif
            // still seeds an image clip but behaves like a video here (see originIsGif).
            documentMode: this.startMode || ((this.originSourceType === CLIP_SOURCE_TYPES.IMAGE && !this.originIsGif) ? 'photo' : 'video')
        }
    },

    computed: {
        isPhotoMode() {
            return this.documentMode === 'photo'
        },

        // Same rule as documentMode's own initial value - which mode this document actually
        // started as, regardless of whatever it's since been toggled to.
        originMode() {
            return (this.originSourceType === CLIP_SOURCE_TYPES.IMAGE && !this.originIsGif) ? 'photo' : 'video'
        },

        // Toggling *back* to the mode this document originated as reads as "revert" (Repasser) -
        // toggling *away* from it, into a mode it never actually was, reads as "convert" (Passer).
        // Wording it the same regardless of origin ("Repasser en mode photo" on a video that's
        // never been a photo) implied undoing something that never happened.
        modeToggleLabelKey() {
            const targetMode = this.isPhotoMode ? 'video' : 'photo'
            const isBackToOrigin = targetMode === this.originMode
            if (targetMode === 'video') {
                return isBackToOrigin
                    ? 'dash.post-creator.media-editor.editor.mode-back-to-video'
                    : 'dash.post-creator.media-editor.editor.mode-switch-to-video'
            }
            return isBackToOrigin
                ? 'dash.post-creator.media-editor.editor.mode-switch-to-photo'
                : 'dash.post-creator.media-editor.editor.mode-forward-to-photo'
        },

        canUndo() {
            return this.historyIndex > 0
        },
        canRedo() {
            return this.historyIndex < this.history.length - 1
        },
        mediaTrack() {
            return this.timeline ? getMediaTrack(this.timeline) : null
        },
        overlayTrack() {
            return this.timeline ? getOverlayTrack(this.timeline) : null
        },
        // the whole media track's clip list (a timeline can combine several source clips into
        // one output), plus the overlay/canvas on top - null while any referenced clip is still
        // a local blob: url (added this session, not yet uploaded), since that can't survive
        // outside this browser tab and would make the template unusable later
        saveMediaTemplateData() {
            if (!this.timeline || !this.mediaTrack || !this.overlayTrack) return null
            if (!this.mediaTrack.clips.length) return null

            const files = []
            for (const clip of this.mediaTrack.clips) {
                const entry = this.fileRegistry[clip.fileId]
                if (!entry || !entry.url || entry.url.startsWith('blob:')) return null
                files.push({ url: entry.url, mimetype: (entry.localFile && entry.localFile.type) || null })
            }

            return {
                data: {
                    files,
                    overlay: {
                        canvas: deepClone(this.timeline.canvas),
                        overlays: deepClone(this.overlayTrack.overlays)
                    }
                },
                source: 'media-editor'
            }
        },
        audioTrack() {
            return this.timeline ? getAudioTrack(this.timeline) : null
        },
        fileNames() {
            const map = {}
            Object.keys(this.fileRegistry).forEach((fileId) => {
                const entry = this.fileRegistry[fileId]
                map[fileId] = (entry.localFile && entry.localFile.name) || this.$t('dash.post-creator.media-editor.editor.fallback-music-name')
            })
            return map
        },
        durationMs() {
            return this.timeline ? computeTimelineDurationMs(this.timeline) : 0
        },
        selectedClip() {
            if (!this.mediaTrack) return null
            return this.mediaTrack.clips.find(c => c.id === this.selectedClipId) || null
        },
        selectedOverlay() {
            if (!this.overlayTrack) return null
            return this.overlayTrack.overlays.find(o => o.id === this.selectedOverlayId) || null
        },
        // Available in both fill modes - dragging always customizes cropRect's ratio/position (see
        // applyCropRatio's comment); only how it's rendered differs by mode.
        cropSelectorClip() {
            return this.activeClipTool === 'crop' ? this.selectedClip : null
        },
        sources() {
            const map = {}
            Object.keys(this.fileRegistry).forEach((fileId) => { map[fileId] = this.fileRegistry[fileId].url })
            return map
        },

        // Whichever of OverlayConfigPanel/MusicPanel/ClipToolPanel is currently rendered (they're
        // mutually exclusive - see the v-if/v-else-if chain above), all sharing the same
        // ref="sidePanel" name so this doesn't need to know which one it actually is. Also true for
        // a canvas-only group/multi-selection (no panel of its own, just the floating toolbar) -
        // without it, clicking outside never attaches the listener that would deselect it.
        hasOpenSidePanel() {
            return !!(this.selectedOverlay || this.activeClipTool || this.hasMultiSelection)
        }
    },

    watch: {
        hasOpenSidePanel(isOpen) {
            if (isOpen) document.addEventListener('mousedown', this.onSidePanelOutsideClick)
            else document.removeEventListener('mousedown', this.onSidePanelOutsideClick)
        },

        // Leaving the silence tool (a different tool, or closing the panel) without an explicit
        // "Valider" discards whatever was being reviewed, rather than leaving it half-applied.
        activeClipTool(tool) {
            if (tool !== 'silence') this.silencePreview = null
            // Mirror of the selectedOverlayId watcher below, which closes any open clip tool: an
            // overlay left selected would win the side panel's v-if chain and hide the clip tool
            // that was just opened, and the crop tool in particular hides the overlay layer the
            // config panel would still be editing (see VideoPreviewCanvas.vue's OverlayCanvas).
            if (tool) this.clearOverlaySelection()
        },

        // Switching to a different clip while reviewing one clip's silences discards that review
        // too - it was never about *this* clip in the first place.
        selectedClipId(id) {
            if (this.silencePreview && this.silencePreview.clipId !== id) this.silencePreview = null
        },

        // Editing a widget should show the moment it's actually on screen - jump the playhead
        // into its active window if it isn't already there.
        selectedOverlayId(id) {
            if (id) this.activeClipTool = null
            // Adding a new overlay (onPlaceOverlay sets this to the new one's id) or selecting an
            // existing one to edit both need the timeline open - its tracks are the only place to
            // see/retime the thing being added or edited, which a closed (playback-only, see
            // timeline.scss) timeline can't show at all.
            if (id) this.timelineExpanded = true
            if (!this.$refs.preview) return

            if (id && this.selectedOverlay) {
                const overlay = this.selectedOverlay
                if (this.currentMs < overlay.startMs || this.currentMs > overlay.endMs) {
                    this.$refs.preview.seek(overlay.startMs)
                }
            }

            // A null here can mean three different things, and only one of them should ever echo
            // back into the canvas:
            // 1. The canvas already deselected itself on its own, or some other caller
            //    (closeOverlayConfig, removeOverlay...) already cleared it directly - nothing left
            //    to do, echoing again is a harmless no-op.
            // 2. A brand new rubber-band/drag gesture is starting on the very same click that
            //    cleared the old selection - echoing the deselect back in at that exact moment used
            //    to reset fabric's own in-progress gesture tracking ("selection not registered,
            //    have to start over"). hasActiveGesture(), checked after the $nextTick delay (by
            //    which point fabric's own mousedown handling has already set up that gesture state
            //    if one is starting), is what catches this.
            // 3. A legitimate 2+ multi-selection (Ctrl+click, rubber-band) just replaced the single
            //    selection - onSelectionChanged emits `select: null` for that too (a multi-selection
            //    has no single overlay to drive the config panel with), but the ActiveSelection
            //    itself is exactly what the user is actively building, e.g. right before grouping
            //    it. Ctrl+click in particular sets neither _groupSelector nor _currentTransform (no
            //    handle grabbed, no drag), so hasActiveGesture() alone doesn't catch this case -
            //    echoing selectOverlay(null) here would discard the selection the same click just
            //    built. getSelectedOverlayIds().length distinguishes it: 2+ means leave it alone.
            if (!id) {
                this.$nextTick(() => {
                    if (!this.$refs.preview || this.$refs.preview.hasActiveGesture()) return
                    if (this.$refs.preview.getSelectedOverlayIds().length <= 1) this.$refs.preview.selectOverlay(null)
                })
                return
            }
            // Deferred: seeking above (when it runs) recomputes every overlay's visibility for the
            // new currentMs on the canvas side (OverlayCanvas's own currentMs watcher) before this
            // fires - selecting the object first, synchronously, would grab it while it's still
            // marked invisible from the *old* time, leaving no selection border on screen even
            // though it's genuinely the active object internally. This is exactly what selecting an
            // overlay from the timeline (as opposed to clicking it directly on the canvas, where
            // it's already visible/on-screen by definition) actually hits.
            this.$nextTick(() => {
                if (this.$refs.preview) this.$refs.preview.selectOverlay(id)
            })
        }
    },

    async mounted() {
        let originUrl = this.videoSrc
        // A saved post's image is a remote URL, and loading it as <img crossOrigin="anonymous"> fails
        // (black preview) without CORS headers or on a non-CORS cache hit - so work from a local
        // blob (as ThumbnailSelector does). Images only: videos are too heavy to download here.
        if (this.originSourceType === CLIP_SOURCE_TYPES.IMAGE && !/^(blob|data):/.test(originUrl)) {
            try {
                const blob = this.localFile || await (await fetch(originUrl, { cache: 'no-store', mode: 'cors' })).blob()
                originUrl = this.originBlobUrl = URL.createObjectURL(blob)
            } catch (error) {
                console.error('[MediaEditor] could not fetch origin image as blob, falling back to its URL', error)
            }
        }
        // $set (not plain assignment) - fileRegistry is reactive, but Vue 2 can't detect a brand
        // new property being added to an existing object via `obj[key] = value`. Without $set,
        // the `sources` computed never re-evaluates for a fileId added after the very first
        // render, so any clip registered later (an added image, an added video, background
        // music...) resolved to `sources[fileId] === undefined` forever - a broken image icon in
        // the timeline, or a video with no playable src.
        this.$set(this.fileRegistry, ORIGIN_FILE_ID, { kind: this.originSourceType, url: originUrl, localFile: this.localFile })

        Object.keys(this.extraFiles || {}).forEach((fileId) => {
            const file = this.extraFiles[fileId]
            const kind = file.type.startsWith('image/') ? 'image' : (file.type.startsWith('audio/') ? 'audio' : 'video')
            this.$set(this.fileRegistry, fileId, { kind, url: URL.createObjectURL(file), localFile: file })
        })

        // An image origin (see the "Créer une vidéo" bridge from ImageCropper.vue) has no
        // duration/fps/bitrate to probe - readVideoMetadata on an image URL would just hang/error
        // via <video>.onerror, losing its real dimensions in the process.
        if (this.originSourceType === CLIP_SOURCE_TYPES.IMAGE) {
            this.originProbe = await this.readImageMetadata(originUrl)
        } else {
            const [videoMeta, sourceStats] = await Promise.all([
                this.readVideoMetadata(originUrl),
                probeSourceMediaStats(this.localFile, originUrl)
            ])
            this.originProbe = { ...videoMeta, fps: chooseExportFps(sourceStats.fps), sourceBitrate: sourceStats.bitrate }
        }
        this.timeline = this.buildInitialTimeline(this.originProbe)
        // Restores which instant photo mode last captured (see toggleDocumentMode/timelineModel.js's
        // photoFrameMs). Setting this.currentMs alone isn't enough - VideoPreviewCanvas.vue tracks
        // its own separate currentMs (its TimelinePlayer always starts at ms 0), so the preview
        // must be seeked directly once it exists.
        if (this.isPhotoMode && this.timeline.photoFrameMs) {
            this.currentMs = this.timeline.photoFrameMs
            this.$nextTick(() => {
                if (this.$refs.preview) this.$refs.preview.seek(this.currentMs)
            })
        }
        if (this.mediaTrack && this.mediaTrack.clips.length) this.selectedClipId = this.mediaTrack.clips[0].id
        // The toolbox opens on the "Ratio" tool by default rather than closed, since choosing a
        // format is the very first thing most edits start with.
        if (this.selectedClipId) this.onSelectClipTool('crop')
        // Seeds undo history with the starting state itself, so undoing the very first edit has
        // something to land back on.
        this.history = [cloneTimeline(this.timeline)]
        this.historyIndex = 0
        this.initialTimelineJson = JSON.stringify(this.timeline)
        this.initialDocumentMode = this.documentMode
        window.addEventListener('keydown', this.onKeydown)
    },

    beforeDestroy() {
        // Only revoke URLs this component created itself (extra clips registered mid-session) -
        // the origin's URL is owned and managed by Form.vue, not us (except originBlobUrl, the
        // local copy of a remote origin image that mounted() made itself).
        if (this.originBlobUrl) URL.revokeObjectURL(this.originBlobUrl)
        Object.keys(this.fileRegistry).forEach((fileId) => {
            if (fileId === ORIGIN_FILE_ID) return
            URL.revokeObjectURL(this.fileRegistry[fileId].url)
        })
        clearTimeout(this.historyPushTimer)
        document.removeEventListener('mousedown', this.onSidePanelOutsideClick)
        window.removeEventListener('keydown', this.onKeydown)
    },

    methods: {
        // Clicking anywhere outside the open side panel closes it - the panel's own explicit
        // close button (see ClipToolPanel/OverlayConfigPanel/MusicPanel's ".ocp-close") only
        // covers clicking that one spot; this covers just empty popup space, so a config never
        // lingers open once the user's attention has clearly moved elsewhere.
        //
        // The whole Timeline *and* the whole preview canvas are excluded outright, not just the
        // panel itself - a @click-based trigger (a tool icon, the transition chip) is safe either
        // way, since click fires *after* mousedown and so always wins over whatever this closes
        // first. But something that must select on mousedown itself, to support click-and-drag in
        // the same gesture, opens the *same* mousedown this listener reacts to - and since this
        // listener sits on document, it always runs *after* that handler in the bubble phase, so
        // without excluding it entirely it would immediately re-close whatever had just been
        // asked to open, in the very same event. That's true for an overlay/audio row on the
        // Timeline (OverlayTrackRow/AudioTrackRow) *and* for dragging a selected overlay directly
        // on the canvas itself (Fabric's own mousedown starts the drag) - both already manage
        // their own selection state precisely on their own (Fabric's selection:cleared/created,
        // the rows' own @select emits), this generic listener has nothing useful left to add
        // there and only gets in the way.
        onSidePanelOutsideClick(e) {
            if (this.$refs.sidePanel && this.$refs.sidePanel.$el.contains(e.target)) return
            if (this.$refs.timeline && this.$refs.timeline.$el.contains(e.target)) return
            if (this.$refs.preview && this.$refs.preview.$el.contains(e.target)) return
            this.clearOverlaySelection()
            this.activeClipTool = null
        },

        readVideoMetadata(url) {
            return new Promise((resolve) => {
                const probe = document.createElement('video')
                probe.preload = 'metadata'
                probe.onloadedmetadata = () => resolve({
                    durationMs: Math.round(probe.duration * 1000),
                    width: probe.videoWidth,
                    height: probe.videoHeight
                })
                probe.onerror = () => resolve({ durationMs: 0, width: 0, height: 0 })
                probe.src = url
            })
        },

        buildInitialTimeline(probe) {
            const restored = this.tryRestoreTimeline()
            return restored || this.buildFreshTimeline(probe)
        },

        // Fresh, single-clip timeline built straight from the original source - used both as the
        // first-ever open and by the explicit "revenir à l'original" button.
        buildFreshTimeline({ durationMs: sourceDurationMs, width, height, fps, sourceBitrate }) {
            // Start the export canvas at (a capped) *native* resolution rather than a fixed
            // baseline - otherwise any source larger than that baseline (e.g. a 4K upload) was
            // being downscaled from the very first frame, before the user ever touched anything.
            const { width: canvasWidth, height: canvasHeight } = width && height
                ? capResolution(width, height)
                : { width: 1080, height: 1920 }

            // Matching the source's own frame rate (rather than always forcing 30fps) avoids the
            // export loop repeatedly seeking to the same decoded source frame at uneven intervals -
            // that mismatch is what reads back as visible judder on 24/25/50/60fps footage.
            const timeline = createEmptyTimeline({ width: canvasWidth, height: canvasHeight, fps: fps || 30 })
            // The bitrate formula alone can undershoot a source that was already encoded richer
            // than its own resolution/fps would suggest (small/modest frame sizes especially) -
            // computeVideoBitrate takes the max against this so re-encoding never quality-downgrades
            // fine detail (on-screen text baked into the footage is the most visible casualty) the
            // source already had.
            if (sourceBitrate) timeline.exportSettings.videoBitrate = sourceBitrate
            const clip = this.originSourceType === CLIP_SOURCE_TYPES.IMAGE
                ? createImageClip({ fileId: ORIGIN_FILE_ID })
                : createVideoClip({ fileId: ORIGIN_FILE_ID, sourceDurationMs, trimStartMs: 0, trimEndMs: sourceDurationMs })
            clip.sourceWidth = width
            clip.sourceHeight = height
            addClipToTrack(timeline, getMediaTrack(timeline).id, clip)
            return timeline
        },

        // Only safe to restore if every referenced clip/audio fileId is still resolvable - either
        // the origin clip, or an extra clip/audio file handed back in via the extraFiles prop.
        tryRestoreTimeline() {
            if (!this.timelineState) return null
            try {
                const restored = cloneTimeline(this.timelineState)
                const mediaTrack = getMediaTrack(restored)
                const audioTrack = getAudioTrack(restored)
                const allFileIds = [
                    ...(mediaTrack ? mediaTrack.clips.map(c => c.fileId) : []),
                    ...(audioTrack ? audioTrack.clips.map(c => c.fileId) : [])
                ]
                const allResolvable = allFileIds.every(id => id === ORIGIN_FILE_ID || this.fileRegistry[id])
                return allResolvable ? restored : null
            } catch (e) {
                return null
            }
        },

        // Discards every edit (added clips, crops, overlays, audio, ...) and rebuilds a fresh
        // timeline from the original, untouched source video.
        requestRevert() {
            this.showRevertConfirm = true
        },

        revertToOriginal() {
            this.showRevertConfirm = false
            this.timeline = this.buildFreshTimeline(this.originProbe)
            this.clearOverlaySelection()
            this.activeClipTool = null
            this.selectedClipId = this.mediaTrack && this.mediaTrack.clips.length ? this.mediaTrack.clips[0].id : null
            this.markDirty()
            this.refreshPreview()
        },

        resolveSource(fileId) {
            return this.fileRegistry[fileId] || null
        },

        registerFile(file) {
            const fileId = `file_${Math.random().toString(36).substr(2, 9)}`
            const kind = file.type.startsWith('image/') ? 'image' : (file.type.startsWith('audio/') ? 'audio' : 'video')
            this.$set(this.fileRegistry, fileId, { kind, url: URL.createObjectURL(file), localFile: file })
            return { fileId, kind }
        },

        // index undefined appends at the end (the timeline's own "+" button) - any other value
        // inserts right there instead (a clip's own hover-revealed "insert after me" control).
        async addClip({ file, index } = {}) {
            const { fileId, kind } = this.registerFile(file)
            let clip, width, height
            if (kind === 'image') {
                clip = createImageClip({ fileId })
                const dimensions = await this.readImageMetadata(this.fileRegistry[fileId].url)
                width = dimensions.width
                height = dimensions.height
            } else {
                const { durationMs: sourceDurationMs, width: videoWidth, height: videoHeight } = await this.readVideoMetadata(this.fileRegistry[fileId].url)
                clip = createVideoClip({ fileId, sourceDurationMs, trimStartMs: 0, trimEndMs: sourceDurationMs })
                width = videoWidth
                height = videoHeight
            }

            // Without this, an image clip's crop tool (ClipToolPanel.vue) never got past its own
            // "Chargement des dimensions..." guard (v-if="!clip.sourceWidth") - only the video
            // branch here ever set these, so no per-network ratio/crop was ever reachable for an
            // image clip at all, only for video ones.
            clip.sourceWidth = width
            clip.sourceHeight = height

            // Sources can have wildly different native ratios (a 9:16 phone clip next to a 16:9
            // screen recording, etc.) - a clip added after the timeline has already settled on a
            // format (via clip 1's crop choice) should match that format by default, not start
            // "original" at its own unrelated native ratio. Applies the same way whether the new
            // clip is a video or an image.
            if (width && height && this.mediaTrack.clips.length > 0) {
                const targetRatio = this.timeline.canvas.width / this.timeline.canvas.height
                clip.transform.cropRect = computeCropRectForRatio(width, height, targetRatio)
                clip.aspectRatioTarget = this.timeline.canvas.aspectRatioLabel || targetRatio
            }

            addClipToTrack(this.timeline, this.mediaTrack.id, clip, index)
            this.selectedClipId = clip.id
            this.markDirty()
            this.refreshPreview()
        },

        // Re-fetches each of the template's files into a real File (same technique already used
        // for a url-based file elsewhere) and adds them as new clips via the existing addClip -
        // appends to the timeline rather than replacing the origin, which stays a safer operation
        // than swapping the live source out from under an already-mounted editing session.
        async applyMediaTemplate(template) {
            this.showMediaTemplatePicker = false
            if (!this.timeline || !template.data) return

            const files = Array.isArray(template.data.files) ? template.data.files : []
            for (const f of files) {
                if (!f.url) continue
                try {
                    const response = await fetch(f.url)
                    const blob = await response.blob()
                    const file = new File([blob], 'media', { type: f.mimetype || blob.type })
                    await this.addClip({ file })
                } catch (error) {
                    console.error('Error fetching template media file:', error)
                }
            }

            if (template.data.overlay) {
                const overlay = deepClone(template.data.overlay)

                if (overlay.canvas) {
                    if (overlay.canvas.backgroundColor) this.timeline.canvas.backgroundColor = overlay.canvas.backgroundColor
                    if (overlay.canvas.backgroundMode) this.timeline.canvas.backgroundMode = overlay.canvas.backgroundMode
                }

                (overlay.overlays || []).forEach((o) => {
                    o.id = generateId('overlay')
                    o.endMs = Math.min(o.endMs, this.durationMs)
                    o.startMs = Math.min(o.startMs, o.endMs)
                    addOverlayToTrack(this.timeline, o)
                })

                this.markDirty()
                this.refreshPreview()
            }
        },

        onDragEnter(e) {
            // Ignore a drag that isn't carrying files at all (e.g. dragging selected text/an
            // element around the page) - only a real file drag should trigger the drop overlay.
            if (!e.dataTransfer || !e.dataTransfer.types || !e.dataTransfer.types.includes('Files')) return
            this.dragCounter++
            this.isDraggingFileOver = true
        },

        onDragLeave() {
            this.dragCounter--
            if (this.dragCounter <= 0) {
                this.dragCounter = 0
                this.isDraggingFileOver = false
            }
        },

        // Dropped anywhere in the editor - always appended at the end of the main track (index
        // undefined, same convention as addClip above), not inserted at some position derived from
        // where the cursor happened to be over the timeline.
        async onFilesDropped(e) {
            this.dragCounter = 0
            this.isDraggingFileOver = false
            if (!this.timeline) return
            const files = Array.from((e.dataTransfer && e.dataTransfer.files) || [])
                .filter(file => file.type.startsWith('video/') || file.type.startsWith('image/'))
            // Sequential (not Promise.all) - each addClip reads this.mediaTrack.clips.length to
            // decide whether to match the timeline's existing ratio, and pushes its own undo
            // snapshot; running them concurrently would race on both.
            for (const file of files) {
                await this.addClip({ file })
            }
        },

        // A plain solid-color image clip (no upload) - useful as a spacer/title-card slide inserted
        // straight from the timeline. Reuses the exact same registerFile/createImageClip path as a
        // real uploaded image by wrapping the generated blob in a File, so nothing downstream (the
        // config panel, export, resolveSource) needs to know it isn't one. Same index convention
        // as addClip above (undefined = append, otherwise insert right there). color comes from the
        // picker in AddClipMenu.vue - falls back to the canvas background only if somehow missing.
        async addBlankImageClip({ index, color } = {}) {
            const canvas = document.createElement('canvas')
            canvas.width = this.timeline.canvas.width
            canvas.height = this.timeline.canvas.height
            const ctx = canvas.getContext('2d')
            ctx.fillStyle = color || this.timeline.canvas.backgroundColor || '#000000'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
            const file = new File([blob], 'blank-image.png', { type: 'image/png' })

            const { fileId } = this.registerFile(file)
            const clip = createImageClip({ fileId })
            // Already known exactly (this canvas is what was just drawn and encoded above) - no
            // need for readImageMetadata's async probe. Without this, the crop tool never got past
            // its own "Chargement des dimensions..." guard for a blank image clip either.
            clip.sourceWidth = canvas.width
            clip.sourceHeight = canvas.height
            addClipToTrack(this.timeline, this.mediaTrack.id, clip, index)
            this.selectedClipId = clip.id
            this.markDirty()
            this.refreshPreview()
        },

        removeClip(clipId) {
            removeClipFromTrack(this.timeline, this.mediaTrack.id, clipId)
            if (this.selectedClipId === clipId) this.selectedClipId = null
            this.markDirty()
            this.refreshPreview()
        },


        onUpdateClip({ clipId, patch }) {
            const clip = this.mediaTrack.clips.find(c => c.id === clipId)
            if (!clip) return
            Object.assign(clip, patch)
            if (patch.trimStartMs !== undefined || patch.trimEndMs !== undefined) {
                clip.segments = [{ startMs: 0, endMs: clip.trimEndMs - clip.trimStartMs, enabled: true }]
                clip.silenceRanges = []
            }
            recomputeTimelineLayout(this.timeline)
            this.markDirty()
            this.refreshPreview()
        },

        // Picking a ratio must also resize the *output canvas* to that ratio - otherwise the
        // crop rect gets computed correctly but is then re-cropped by the old canvas shape when
        // drawn (cover-fit into a still-9:16 frame), which looked like a pointless zoom instead
        // of an actual format change. The reference area comes from the clip's own (capped)
        // native resolution rather than a fixed baseline, to avoid needlessly softening detail.
        // The exact ratio (pre-rounding) is stashed on the canvas so later clips can match it
        // precisely instead of re-deriving a slightly-off value from the rounded pixel dimensions.
        resizeCanvasToRatio(ratio, referenceClip, label) {
            const hasSource = referenceClip && referenceClip.sourceWidth && referenceClip.sourceHeight
            const referenceArea = hasSource
                ? (() => {
                    const capped = capResolution(referenceClip.sourceWidth, referenceClip.sourceHeight)
                    return capped.width * capped.height
                })()
                : 1080 * 1920

            // AVC/H.264 (the export encoder) requires even width/height - rounding to the nearest
            // even number here, not just at export time, keeps editing and export in sync (this
            // canvas size also drives every overlay's xPct/yPct).
            const width = Math.round(Math.sqrt(referenceArea * ratio) / 2) * 2
            const height = Math.round(width / ratio / 2) * 2
            this.timeline.canvas.width = width
            this.timeline.canvas.height = height
            // $set - createEmptyTimeline declares both keys, but a draft restored from before they
            // existed (tryRestoreTimeline hands back the persisted object as-is) has neither.
            this.$set(this.timeline.canvas, 'aspectRatio', ratio)
            this.$set(this.timeline.canvas, 'aspectRatioLabel', label !== undefined ? label : ratio)
            this.timeline.exportSettings.resolution = { width, height }
        },

        // Ratio choices are per-clip: clips are allowed to have different native ratios/crops (a
        // 9:16 phone clip next to a 16:9 screen recording, etc). Only the *canvas* format - the
        // shared output frame every clip is composited into - is a single timeline-wide value,
        // and it follows whichever clip's ratio was last explicitly picked.
        //
        // A ratio *preset* button forks on this clip's own fillMode: Ajuster (contain, default)
        // never crops anything on its own initiative - picking a preset here only changes the
        // canvas shape, cropRect stays/resets to the full original, so the whole video stays
        // visible (letterboxed/pillarboxed to the new ratio). Remplir (cover) computes the max
        // centered crop matching that ratio, which fills the canvas edge to edge. Either way,
        // dragging or typing width/height px afterward (see onUpdateCropRect/setCropSize) always
        // sets the exact real cropRect requested, in BOTH modes - only the preset buttons get this
        // fork, since "give me a well-known format" and "let me pick exactly this region" are
        // different asks. renderClipFrame (canvasCompositor.js) then just draws each clip's own
        // real cropRect, fit per its own fillMode - nothing here ever touches another clip's data,
        // so a clip nobody's editing keeps its own crop and simply adapts (letterbox/pillarbox) to
        // whatever the shared canvas format ends up being.
        applyCropRatio({ clipId, ratio }) {
            const clip = this.mediaTrack.clips.find(c => c.id === clipId)
            if (!clip || !clip.sourceWidth || !clip.sourceHeight) return

            // $set throughout - a draft saved before this feature can have a transform object with
            // no cropRect key, which plain assignment would add invisibly to Vue 2.
            if (ratio === null) {
                this.$set(clip.transform, 'cropRect', { x: 0, y: 0, width: 1, height: 1 })
                clip.aspectRatioTarget = 'custom'
                this.resizeCanvasToRatio(clip.sourceWidth / clip.sourceHeight, clip, 'custom')
            } else if ((clip.transform.fillMode || 'contain') === 'cover') {
                this.$set(clip.transform, 'cropRect', computeCropRectForRatio(clip.sourceWidth, clip.sourceHeight, ratio))
                clip.aspectRatioTarget = ratio
                this.resizeCanvasToRatio(ratio, clip, ratio)
            } else {
                this.$set(clip.transform, 'cropRect', { x: 0, y: 0, width: 1, height: 1 })
                clip.aspectRatioTarget = ratio
                this.resizeCanvasToRatio(ratio, clip, ratio)
            }
            this.markDirty()
            this.refreshPreview()
        },

        // $set (not plain assignment) - fillMode doesn't exist on transform until the first time
        // this runs, and Vue 2 can't detect a brand new property added via plain assignment, so
        // anything reactively depending on it (the Ajuster/Remplir buttons' own active state,
        // VideoPreviewCanvas.vue's cropWorkspaceSize) would silently keep using a stale cached
        // value instead of noticing the change.
        onUpdateFillMode({ clipId, mode }) {
            const clip = this.mediaTrack.clips.find(c => c.id === clipId)
            if (!clip) return
            this.$set(clip.transform, 'fillMode', mode)

            // The same format means a different *selection* in each mode - see applyCropRatio's
            // fork: Remplir wants the ratio-matched region that fills the frame, Ajuster the whole
            // source. Re-applying the active preset here lands on the right one immediately;
            // without it the previous mode's crop stayed behind, so switching to Remplir left a
            // selection box wrapped around the entire image (with the dashed box marking the much
            // smaller part of it that would actually survive) until the first corner drag snapped
            // it to the format. Nothing to re-apply when no preset is active: a free crop already
            // defines the canvas ratio itself (see commitCropRect), so the two always match there
            // and neither mode has a different answer.
            if (typeof clip.aspectRatioTarget === 'number') {
                this.applyCropRatio({ clipId, ratio: clip.aspectRatioTarget })
                return
            }
            this.markDirty()
            this.refreshPreview()
        },

        // Live half of a CropSelector drag - fires on every mousemove, so it does the absolute
        // minimum: write the crop, nothing else. Everything with a side effect on the *canvas*
        // (and therefore on the very coordinate space CropSelector.vue's containBox measures the
        // drag in) waits for commitCropRect below.
        //
        // This used to also set aspectRatioTarget='custom' and resize the canvas to the crop's own
        // ratio per tick, which closed a feedback loop: crop -> canvas -> the box the crop is drawn
        // in. Picking 1:1 in Ajuster (which resets cropRect to the full source, so the drag clamps
        // can't actually move anything) still un-highlighted the 1:1 button and snapped the canvas
        // back to the source's native ratio on the first pixel of drag - the format silently undone,
        // and the letterboxed square turning edge-to-edge, which reads as the fill mode flipping on
        // its own.
        //
        // isDirty directly rather than markDirty(): the model is already mutated here, so a drag
        // whose mouseup never reaches the page (released outside the browser window) must still
        // count as unsaved work - but the history push belongs to the commit, one entry per drag.
        // $set - a draft saved before this feature can have a transform with no cropRect key at all.
        onUpdateCropRect(rect) {
            if (!this.selectedClip) return
            this.$set(this.selectedClip.transform, 'cropRect', rect)
            this.isDirty = true
        },

        // End of a crop gesture (mouseup, or immediately after a width/height px input - see
        // onCropRectInput). Only here does the crop get to redefine the output format, and only when
        // no ratio preset is active: a preset is an explicit "I want this format" that dragging the
        // crop inside it must not override. typeof !== 'number' rather than === 'custom' so old
        // drafts (no aspectRatioTarget at all) and the legacy '9:16' string on image clips both fall
        // into the free case instead of silently locking the canvas.
        commitCropRect() {
            const clip = this.selectedClip
            if (!clip || !clip.sourceWidth || !clip.sourceHeight) return

            if (typeof clip.aspectRatioTarget !== 'number') {
                const rect = clip.transform.cropRect || { x: 0, y: 0, width: 1, height: 1 }
                // rect.width/rect.height are fractions of sourceWidth/sourceHeight *respectively* -
                // on a non-square source (e.g. a 1080x1920 portrait clip) their raw ratio is off by
                // exactly sourceWidth/sourceHeight from the crop's actual pixel aspect ratio (confirmed
                // via [CROP-DEBUG]: canvasRatio ended up exactly 16/9 times too wide relative to the
                // crop actually selected). Convert to real pixel dimensions first so the canvas the
                // clip is composited into actually matches the shape just dragged, instead of
                // letterboxing/pillarboxing a crop that was supposed to fill it edge-to-edge.
                const pixelRatio = (rect.width * clip.sourceWidth) / (rect.height * clip.sourceHeight)
                // Skipped when the shape didn't actually change (a pure translate): resizeCanvasToRatio
                // rounds to even pixel dimensions, so re-running it on an unchanged ratio still nudges
                // the canvas by a pixel or two - visible as the frame twitching after every drag.
                const currentRatio = this.timeline.canvas.width / this.timeline.canvas.height
                if (Math.abs(pixelRatio - currentRatio) > 0.001) {
                    this.resizeCanvasToRatio(pixelRatio, clip, 'custom')
                }
            }

            this.markDirty()
            this.refreshPreview()
        },

        // The width/height px inputs (ClipToolPanel.vue's setCropSize) have no drag lifecycle to
        // hang a commit off - each change is already a finished edit.
        onCropRectInput(rect) {
            this.onUpdateCropRect(rect)
            this.commitCropRect()
        },

        // $set (not plain assignment) - a timeline restored from a draft saved before this feature
        // existed (see tryRestoreTimeline) can have a canvas object with no backgroundMode/
        // backgroundColor keys at all, and Vue 2 can't detect a brand new property added via plain
        // assignment: the "Couleur unie" button would flip the underlying value but the mode toggle's
        // own active state and the color-picker row's v-if would never notice - looking like the
        // button just doesn't react.
        onUpdateBackground({ mode, color }) {
            if (mode !== undefined) this.$set(this.timeline.canvas, 'backgroundMode', mode)
            if (color !== undefined) this.$set(this.timeline.canvas, 'backgroundColor', color)
            this.markDirty()
            this.refreshPreview()
        },

        onReorderClips(newClips) {
            this.mediaTrack.clips = newClips
            recomputeTimelineLayout(this.timeline)
            this.markDirty()
            this.refreshPreview()
        },

        onSeek(ms) {
            if (this.$refs.preview) this.$refs.preview.seek(ms)
        },

        toggleFullscreen() {
            if (this.$refs.preview) this.$refs.preview.toggleFullscreen()
        },

        // Timeline's hover-preview toggle - a no-op while actually playing, since previewing an
        // arbitrary frame seeks/switches the player's active media element, which would visibly
        // and audibly hijack real playback instead of just being a harmless peek.
        onPreviewSeek(ms) {
            if (this.playing || !this.$refs.preview) return
            if (ms === null) this.$refs.preview.clearPreview()
            else this.$refs.preview.previewAt(ms)
        },

        // The single, consolidated transport bar now lives in Timeline.vue (previously duplicated
        // between it and VideoPreviewCanvas's own playbar) - VideoPreviewCanvas still owns the
        // actual TimelinePlayer/play state (its playback logic has had enough of its own subtle
        // bugs fixed this session that moving it wasn't worth the risk), it just relays here.
        togglePlay() {
            if (this.$refs.preview) this.$refs.preview.togglePlay()
        },

        // Purely a UI/export flag - the underlying timeline/overlays never change, so this never
        // touches `timeline` itself, aside from recording photoFrameMs below.
        toggleDocumentMode() {
            if (this.documentMode === 'video' && this.playing && this.$refs.preview) this.$refs.preview.togglePlay()
            this.documentMode = this.documentMode === 'photo' ? 'video' : 'photo'
            // Keeps currentMs as the instant the user was actually looking at (finish() exports
            // from it, see its own comment) and records it onto the timeline so a save/resume
            // round-trip restores the same frame (see mounted()).
            if (this.documentMode === 'photo') this.timeline.photoFrameMs = this.currentMs
        },

        onTimeUpdate(ms) {
            this.currentMs = ms
        },

        refreshPreview() {
            this.$nextTick(() => { if (this.$refs.preview) this.$refs.preview.refresh() })
        },

        markDirty() {
            this.isDirty = true
            this.scheduleHistoryPush()
        },

        // Debounced so a burst of edits (dragging a trim handle, a slider, anything that calls
        // markDirty on every tick) collapses into the single resulting state as one history entry,
        // instead of the last several ticks eating most of the 10-entry budget on their own.
        scheduleHistoryPush() {
            if (this.isRestoringHistory) return
            clearTimeout(this.historyPushTimer)
            this.historyPushTimer = setTimeout(this.pushHistory, HISTORY_PUSH_DEBOUNCE_MS)
        },

        pushHistory() {
            if (!this.timeline) return
            // Making a new edit after undoing discards whatever redo-able future was there -
            // standard undo/redo semantics, the same as any text editor.
            this.history = this.history.slice(0, this.historyIndex + 1)
            this.history.push(cloneTimeline(this.timeline))
            if (this.history.length > MAX_HISTORY) this.history.shift()
            this.historyIndex = this.history.length - 1
        },

        restoreHistoryAt(index) {
            if (index < 0 || index >= this.history.length) return
            this.isRestoringHistory = true
            this.historyIndex = index
            // Cloned again on the way out too - this.timeline gets mutated in place by every
            // subsequent edit, and mutating the stored snapshot itself would corrupt history.
            this.timeline = cloneTimeline(this.history[index])
            this.isDirty = true
            this.refreshPreview()
            this.$nextTick(() => { this.isRestoringHistory = false })
        },

        undo() {
            if (this.canUndo) this.restoreHistoryAt(this.historyIndex - 1)
        },

        redo() {
            if (this.canRedo) this.restoreHistoryAt(this.historyIndex + 1)
        },

        // Whichever is currently selected wins - an overlay (text/widget/image/sticker) takes
        // priority over the media clip underneath it since selecting an overlay on the canvas
        // doesn't clear selectedClipId, and copy/delete should act on the more specific selection.
        copySelection() {
            const multiIds = this.$refs.preview ? this.$refs.preview.getSelectedOverlayIds() : []
            if (multiIds.length >= 2) {
                const overlaysById = new Map(this.overlayTrack.overlays.map(o => [o.id, o]))
                this.clipboard = { type: 'overlays', data: multiIds.map(id => deepClone(overlaysById.get(id))).filter(Boolean) }
            } else if (this.selectedOverlay) {
                this.clipboard = { type: 'overlay', data: deepClone(this.selectedOverlay) }
            } else if (this.selectedClip) {
                this.clipboard = { type: 'clip', data: deepClone(this.selectedClip) }
            }
        },

        pasteClipboard() {
            if (!this.clipboard) return
            if (this.clipboard.type === 'overlays') {
                // Same partition-by-groupId logic as onDuplicateOverlaySelection, so pasting a
                // copied group forms its own new group instead of rejoining the original.
                const groupIds = new Set(this.clipboard.data.map(o => o.groupId).filter(Boolean))
                const newGroupIdByOldGroupId = new Map([...groupIds].map(id => [id, generateId('group')]))
                const copyIds = []
                this.clipboard.data.forEach((overlay) => {
                    const copy = duplicateOverlay(overlay, this.durationMs)
                    if (overlay.groupId) copy.groupId = newGroupIdByOldGroupId.get(overlay.groupId)
                    addOverlayToTrack(this.timeline, copy)
                    copyIds.push(copy.id)
                })
                this.reselectOverlaysOnCanvas(copyIds)
            } else if (this.clipboard.type === 'overlay') {
                const copy = duplicateOverlay(this.clipboard.data, this.durationMs)
                addOverlayToTrack(this.timeline, copy)
                this.selectedOverlayId = copy.id
            } else if (this.clipboard.type === 'clip' && this.mediaTrack) {
                const copy = duplicateMediaClip(this.clipboard.data)
                const originalIndex = this.mediaTrack.clips.findIndex(c => c.id === this.clipboard.data.id)
                const insertAt = originalIndex >= 0 ? originalIndex + 1 : undefined
                addClipToTrack(this.timeline, this.mediaTrack.id, copy, insertAt)
                this.selectedClipId = copy.id
            }
            this.markDirty()
            this.refreshPreview()
        },

        deleteSelection() {
            const multiIds = this.$refs.preview ? this.$refs.preview.getSelectedOverlayIds() : []
            if (multiIds.length >= 2) this.onRemoveOverlaySelection(multiIds)
            else if (this.selectedOverlayId) this.removeOverlay(this.selectedOverlayId)
            else if (this.selectedClipId) this.removeClip(this.selectedClipId)
        },

        // Global shortcuts (delete/undo/redo/copy/paste) - skipped while focus is on a real text
        // input/textarea/contenteditable (an overlay's own text field, the crop tool's width/
        // height inputs...) so typing "suppr un mot" or Ctrl+Z inside a text field doesn't get
        // hijacked into deleting the clip/overlay instead of editing its own text.
        onKeydown(e) {
            const targetTag = (e.target.tagName || '').toLowerCase()
            if (targetTag === 'input' || targetTag === 'textarea' || e.target.isContentEditable) return

            const ctrlOrCmd = e.ctrlKey || e.metaKey
            if (!ctrlOrCmd && (e.key === 'Delete' || e.key === 'Backspace')) {
                e.preventDefault()
                this.deleteSelection()
            } else if (ctrlOrCmd && e.key.toLowerCase() === 'z' && !e.shiftKey) {
                e.preventDefault()
                this.undo()
            } else if (ctrlOrCmd && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
                e.preventDefault()
                this.redo()
            } else if (ctrlOrCmd && e.key.toLowerCase() === 'c') {
                e.preventDefault()
                this.copySelection()
            } else if (ctrlOrCmd && e.key.toLowerCase() === 'v') {
                e.preventDefault()
                this.pasteClipboard()
            } else if (!ctrlOrCmd && e.key === 'Escape') {
                this.clearOverlaySelection()
            } else if (ctrlOrCmd && e.key.toLowerCase() === 'a') {
                e.preventDefault()
                if (this.overlayTrack && this.$refs.preview) this.$refs.preview.selectOverlays(this.overlayTrack.overlays.map(o => o.id))
            } else if (ctrlOrCmd && e.key.toLowerCase() === 'g') {
                e.preventDefault()
                const ids = this.$refs.preview ? this.$refs.preview.getSelectedOverlayIds() : []
                if (ids.length < 2) return
                if (e.shiftKey) {
                    const overlay = this.overlayTrack.overlays.find(o => o.id === ids[0])
                    if (overlay && overlay.groupId) this.onUngroupOverlays(overlay.groupId)
                } else {
                    this.onGroupOverlays(ids)
                }
            }
        },

        // Clearing selectedOverlayId alone only relays down to the fabric canvas when it's a *real*
        // selection - the watcher deliberately never echoes a null back (see its own comment: doing
        // so right as a new rubber-band gesture starts building its own selection is what used to
        // reset it mid-drag). So every place that clears the selection for a reason other than the
        // canvas already having deselected itself - closing the panel, reverting, switching tools,
        // removing the selected overlay - has to tell fabric directly, here.
        clearOverlaySelection() {
            this.selectedOverlayId = null
            if (this.$refs.preview) this.$refs.preview.selectOverlay(null)
        },

        // Reasserted after Vue's next render pass (the config panel unmounting, the timeline
        // re-expanding) in case any of that reflow is what was leaving the canvas's active-object
        // state stuck.
        closeOverlayConfig() {
            this.clearOverlaySelection()
            this.$nextTick(() => {
                if (this.$refs.preview) this.$refs.preview.selectOverlay(null)
            })
        },

        // Crop/rotate/trim are tools in the left palette (not tucked away per-clip) - clicking one
        // opens its controls in the right panel, operating on whichever clip is currently selected
        // (falling back to the clip under the playhead if nothing was explicitly selected yet).
        onSelectClipTool(tool) {
            if (!this.selectedClipId && this.mediaTrack && this.mediaTrack.clips.length) {
                const atPlayhead = this.mediaTrack.clips.find(c => this.currentMs >= c.timelineStartMs && this.currentMs < c.timelineEndMs)
                this.selectedClipId = (atPlayhead || this.mediaTrack.clips[0]).id
            }
            this.clearOverlaySelection()
            this.activeClipTool = tool
            if (tool === 'silence') {
                this.initSilencePreview()
                this.maybeAutoDetectSilence()
            }
        },

        // A clip that's never had a silence pass run against it (no cached profile yet in this
        // session, and nothing committed from an earlier session either - see silenceRanges) gets
        // detection run right away using whatever sensitivity/duration the sliders are already set
        // to, instead of requiring a separate "detect" click first. A clip that's already been
        // processed skips this - initSilencePreview already resumed its real, committed segments,
        // and auto-re-running detection here would silently blow those away.
        maybeAutoDetectSilence() {
            const clip = this.selectedClip
            if (!clip || clip.sourceType !== 'video') return
            if (this.silenceAnalysisCache[clip.id]) return
            if (clip.silenceRanges && clip.silenceRanges.length) return
            this.runSilenceDetection()
        },

        // Divides whichever clip the playhead is currently over into two independent clips right
        // at that point - the clip under the playhead takes priority over a separately selected
        // one, matching where the user is actually looking when they hit "split".
        splitSelectedClip() {
            if (!this.mediaTrack) return
            const clip = this.mediaTrack.clips.find(c => this.currentMs >= c.timelineStartMs && this.currentMs < c.timelineEndMs)
                || this.selectedClip
            if (!clip) return

            const clipCountBefore = this.mediaTrack.clips.length
            splitMediaClip(this.timeline, clip.id, this.currentMs)
            if (this.mediaTrack.clips.length === clipCountBefore) return // too close to an edge - rejected

            this.selectedClipId = clip.id
            this.activeClipTool = null
            this.markDirty()
            this.refreshPreview()
        },

        onClipToolRotation(rotationDeg) {
            if (!this.selectedClip) return
            this.onUpdateClip({ clipId: this.selectedClip.id, patch: { transform: { ...this.selectedClip.transform, rotationDeg } } })
        },

        // A click's *target* is computed from where mouseup lands, not mousedown - per spec, if
        // mousedown started on some descendant (a trim handle, a crop selector, an overlay...)
        // and mouseup ends up back over the backdrop (easy to do mid-drag: the popup doesn't fill
        // the full viewport, and a drag can easily carry the cursor past its edges, or the
        // scroll-area auto-scroll below a busy timeline pushes things around under a held-down
        // drag), the resulting click's target is the nearest common ancestor of the two - which
        // is this backdrop itself, indistinguishable from a genuine "click outside to close"
        // unless mousedown is also checked. Only a mousedown that ALSO began directly on the
        // backdrop (not bubbled up from any child) arms the next click to actually close.
        onBackdropMouseDown(e) {
            this.backdropMouseDownOnly = e.target === e.currentTarget
        },

        onBackdropClick(e) {
            if (e.target === e.currentTarget && this.backdropMouseDownOnly) this.requestClose()
        },

        // Closing (via the X button or a click outside the popup) goes through here rather than
        // emitting clickOutside directly, so an unsaved edit always gets a chance to be kept.
        requestClose() {
            if (this.isDirty) {
                this.showExitConfirm = true
            } else {
                this.$emit('clickOutside')
            }
        },

        confirmDiscard() {
            this.showExitConfirm = false
            this.$emit('clickOutside')
        },

        async confirmSave() {
            this.showExitConfirm = false
            await this.finish()
        },

        // Step 1 of click-to-place: OverlayPalette picked a type/preset, but we don't know where
        // on the video it should go yet - wait for the next click on the canvas (onPlaceOverlay).
        startPlacement(config) {
            this.pendingOverlay = config
            // Whatever clip tool panel was open a moment ago has nothing to do with placing a
            // brand new overlay - close it right away instead of leaving it open (and, thanks to
            // onPlaceOverlay already doing the same once placement completes, showing) until then.
            this.activeClipTool = null
            // An expanded timeline floats over the bottom of the video preview - collapse it so
            // it doesn't sit in the way of the very click that's about to place the element.
            this.timelineExpanded = false
        },

        // Same click-to-place flow as text/shapes/widgets, but the "type config" only exists once
        // the picked file is actually registered (needs a fileId/URL first).
        readImageMetadata(url) {
            return new Promise((resolve) => {
                const probe = new Image()
                probe.onload = () => resolve({ width: probe.naturalWidth, height: probe.naturalHeight })
                probe.onerror = () => resolve({ width: 0, height: 0 })
                probe.src = url
            })
        },

        async onStartImagePlacement(file) {
            const { fileId } = this.registerFile(file)
            const url = this.fileRegistry[fileId].url
            const { width: naturalWidth, height: naturalHeight } = await this.readImageMetadata(url)

            // Starts at 35% of the canvas' width, with height following the image's own natural
            // ratio (not the canvas') so it doesn't look stretched the moment it's placed -
            // sourceWidth/sourceHeight are kept on the overlay's data so the config panel can
            // keep computing that same ratio later when "conserver les proportions" is on.
            const widthPct = 0.35
            const canvasRatio = this.timeline.canvas.width / this.timeline.canvas.height
            const naturalRatio = naturalWidth && naturalHeight ? naturalHeight / naturalWidth : 1
            const heightPct = widthPct * canvasRatio * naturalRatio

            this.startPlacement({
                type: 'image',
                widgetType: null,
                startMs: this.currentMs,
                endMs: Math.min(this.durationMs, this.currentMs + 3000),
                transform: { widthPct, heightPct, lockAspectRatio: true },
                data: { imageUrl: url, fileId, naturalWidth, naturalHeight }
            })
        },

        // Same click-to-place flow, but for moving an *existing* overlay instead of creating a
        // new one - "repositionner en un clic" from the config panel.
        startReposition(overlayId) {
            this.pendingOverlay = { repositionId: overlayId }
        },

        // Step 2: the canvas was clicked while a placement was pending - now we know xPct/yPct.
        // Either move the existing overlay being repositioned, or create a brand new one.
        // Nothing stops the same type being placed again right after (multiple text/widgets of
        // the same kind are fully supported).
        onPlaceOverlay({ xPct, yPct }) {
            if (!this.pendingOverlay) return

            if (this.pendingOverlay.repositionId) {
                const overlay = this.overlayTrack.overlays.find(o => o.id === this.pendingOverlay.repositionId)
                if (overlay) overlay.transform = { ...overlay.transform, xPct, yPct }
            } else {
                const overlay = createOverlay({
                    ...this.pendingOverlay,
                    transform: { ...this.pendingOverlay.transform, xPct, yPct }
                })
                addOverlayToTrack(this.timeline, overlay)
                // A newly-placed element's config should always open, regardless of whatever
                // else was showing in the right panel a moment ago (e.g. the music panel left
                // open from an earlier action) - OverlayConfigPanel only wins its v-if/else-if
                // race against ClipToolPanel/MusicPanel when activeClipTool is falsy.
                this.activeClipTool = null
                this.selectedOverlayId = overlay.id
            }

            this.pendingOverlay = null
            this.markDirty()
            this.refreshPreview()
        },

        onUpdateOverlay({ overlayId, patch }) {
            const overlay = this.overlayTrack.overlays.find(o => o.id === overlayId)
            if (!overlay) return
            Object.assign(overlay, patch)
            this.markDirty()
            this.refreshPreview()
        },

        onOverlayTransformUpdate({ overlayId, transform }) {
            const overlay = this.overlayTrack.overlays.find(o => o.id === overlayId)
            if (!overlay) return
            overlay.transform = { ...overlay.transform, ...transform }
            this.markDirty()
            this.refreshPreview()
        },

        // Double-click-to-edit directly on the preview (see OverlayCanvas.vue's onCanvasDoubleClick
        // and VideoPreviewCanvas.vue's floating input) - the same data patch OverlayConfigPanel's
        // own text/tag/username/location field would have produced, just from the canvas instead.
        onUpdateOverlayData({ overlayId, data }) {
            const overlay = this.overlayTrack.overlays.find(o => o.id === overlayId)
            if (!overlay) return
            overlay.data = { ...overlay.data, ...data }
            this.markDirty()
            this.refreshPreview()
        },

        onMediaError(fileId) {
            if (!this.mediaErrorFileIds.includes(fileId)) this.mediaErrorFileIds.push(fileId)
        },

        // Rebuilds the failed element(s) from scratch (see TimelinePlayer.js's retryMedia) -
        // optimistically clears the banner; if the same fileId fails again, onMediaError above
        // just re-adds it.
        retryFailedMedia() {
            if (!this.mediaTrack || !this.$refs.preview) return
            this.mediaErrorFileIds.forEach((fileId) => {
                const clip = this.mediaTrack.clips.find(c => c.fileId === fileId)
                if (clip) this.$refs.preview.retryMedia(clip.id, fileId)
            })
            this.mediaErrorFileIds = []
        },

        removeOverlay(overlayId) {
            removeOverlayFromTrack(this.timeline, overlayId)
            if (this.selectedOverlayId === overlayId) this.clearOverlaySelection()
            this.markDirty()
            this.refreshPreview()
        },

        // The simple alternative to one overlay's own properties changing partway through the
        // video: a second, independent copy instead, retimed/repositioned/re-edited on its own
        // for whichever other moment it's meant for - see duplicateOverlay's own comment.
        onDuplicateOverlay(overlayId) {
            const overlay = this.overlayTrack.overlays.find(o => o.id === overlayId)
            if (!overlay) return
            const copy = duplicateOverlay(overlay, this.durationMs)
            addOverlayToTrack(this.timeline, copy)
            this.selectedOverlayId = copy.id
            this.markDirty()
            this.refreshPreview()
        },

        reorderOverlay({ overlayId, direction }) {
            reorderOverlayInTrack(this.timeline, overlayId, direction)
            this.markDirty()
            this.refreshPreview()
        },

        // Rebuilding the selection also refreshes the floating toolbar's Grouper/Dégrouper label,
        // which is only ever derived at selection-change time, not reactively tied to overlay data.
        reselectOverlaysOnCanvas(overlayIds) {
            this.$nextTick(() => {
                if (this.$refs.preview) this.$refs.preview.selectOverlays(overlayIds)
            })
        },

        onMultiSelectionChange(payload) {
            this.hasMultiSelection = !!payload
            this.selectedGroupId = payload && payload.isCompleteGroup ? payload.groupId : null
        },

        // Diagnostic entry point (see ElementsTree.vue) - selects the group's members on the
        // canvas exactly like clicking its frame there would, so ElementsTree's own highlight
        // (driven by selectedGroupId, from onMultiSelectionChange) can be compared against
        // whatever the canvas actually renders for the same selection.
        onSelectGroupFromTree(groupId) {
            if (!this.overlayTrack) return
            const memberIds = this.overlayTrack.overlays.filter(o => o.groupId === groupId).map(o => o.id)
            this.reselectOverlaysOnCanvas(memberIds)
        },

        // $set, not plain assignment - a timeline restored from an older session (see
        // tryRestoreTimeline) can have overlays with no groupId key at all (see onUpdateBackground).
        onGroupOverlays(overlayIds) {
            const groupId = generateId('group')
            this.overlayTrack.overlays.forEach((overlay) => {
                if (overlayIds.includes(overlay.id)) this.$set(overlay, 'groupId', groupId)
            })
            this.markDirty()
            this.refreshPreview()
            this.reselectOverlaysOnCanvas(overlayIds)
        },

        onUngroupOverlays(groupId) {
            const memberIds = this.overlayTrack.overlays.filter(o => o.groupId === groupId).map(o => o.id)
            this.overlayTrack.overlays.forEach((overlay) => {
                if (overlay.groupId === groupId) this.$set(overlay, 'groupId', null)
            })
            this.markDirty()
            this.refreshPreview()
            this.reselectOverlaysOnCanvas(memberIds)
        },

        // Copies of a group form their OWN new group, not the original's - each duplicateOverlay
        // call keeps the source groupId by default (see timelineModel.js), so it's overwritten here.
        onDuplicateOverlayGroup(groupId) {
            const members = this.overlayTrack.overlays.filter(o => o.groupId === groupId)
            if (!members.length) return
            const newGroupId = generateId('group')
            const copyIds = []
            members.forEach((overlay) => {
                const copy = duplicateOverlay(overlay, this.durationMs)
                copy.groupId = newGroupId
                addOverlayToTrack(this.timeline, copy)
                copyIds.push(copy.id)
            })
            this.markDirty()
            this.refreshPreview()
            this.reselectOverlaysOnCanvas(copyIds)
        },

        onRemoveOverlayGroup(groupId) {
            const memberIds = this.overlayTrack.overlays.filter(o => o.groupId === groupId).map(o => o.id)
            memberIds.forEach((overlayId) => removeOverlayFromTrack(this.timeline, overlayId))
            if (memberIds.includes(this.selectedOverlayId)) this.clearOverlaySelection()
            this.markDirty()
            this.refreshPreview()
        },

        // Generic multi-select variants (see the canvas floating toolbar) - unlike
        // onDuplicateOverlayGroup/onRemoveOverlayGroup above (which act on one known groupId),
        // these take whatever's currently selected, which can be a mix of loose overlays and one
        // or more complete groups.
        onRemoveOverlaySelection(overlayIds) {
            overlayIds.forEach((overlayId) => {
                removeOverlayFromTrack(this.timeline, overlayId)
                if (this.selectedOverlayId === overlayId) this.clearOverlaySelection()
            })
            this.markDirty()
            this.refreshPreview()
        },

        // Partitions by existing groupId so a duplicated group's copies form their own new shared
        // group (like onDuplicateOverlayGroup), while duplicated loose overlays stay loose.
        onDuplicateOverlaySelection(overlayIds) {
            const overlaysById = new Map(this.overlayTrack.overlays.map(o => [o.id, o]))
            const selected = overlayIds.map(id => overlaysById.get(id)).filter(Boolean)
            const groupIds = new Set(selected.map(o => o.groupId).filter(Boolean))
            const newGroupIdByOldGroupId = new Map([...groupIds].map(id => [id, generateId('group')]))
            const copyIds = []

            selected.forEach((overlay) => {
                const copy = duplicateOverlay(overlay, this.durationMs)
                if (overlay.groupId) copy.groupId = newGroupIdByOldGroupId.get(overlay.groupId)
                addOverlayToTrack(this.timeline, copy)
                copyIds.push(copy.id)
            })
            this.markDirty()
            this.refreshPreview()
            this.reselectOverlaysOnCanvas(copyIds)
        },

        // Background music is a *list* of tracks (not a single slot) - each one keeps its own
        // window on the timeline (timelineStartMs/timelineEndMs) so several can be laid out
        // sequentially, or overlapped on purpose. A newly added track defaults to starting right
        // after whichever track currently ends last (simple sequential default, still fully
        // adjustable via the panel's time-range slider).
        async addBackgroundAudio(file) {
            const { fileId } = this.registerFile(file)
            const sourceDurationMs = await this.readAudioDuration(this.fileRegistry[fileId].url)
            const track = getAudioTrack(this.timeline)
            const lastEnd = track.clips.length ? Math.max(...track.clips.map(c => c.timelineEndMs)) : 0

            track.clips.push(createAudioClip({
                fileId,
                sourceDurationMs,
                timelineStartMs: lastEnd,
                timelineEndMs: lastEnd + sourceDurationMs
            }))

            // A previous track (or several) already filled the whole video - appending normally
            // would've landed the new one squashed to zero width, flush against the very end and
            // effectively unusable. Reflow every track's share of the total duration instead,
            // proportional to each one's own natural length, so they all stay audible.
            if (this.durationMs && lastEnd >= this.durationMs) {
                this.redistributeAudioTracks(track, this.durationMs)
            } else if (this.durationMs) {
                const newClip = track.clips[track.clips.length - 1]
                newClip.timelineEndMs = Math.min(newClip.timelineEndMs, this.durationMs)
                newClip.trimEndMs = newClip.timelineEndMs - newClip.timelineStartMs
            }

            this.markDirty()
            this.refreshPreview()
        },

        // Lays every background music clip out sequentially across [0, durationMs], each getting a
        // slice proportional to its own natural (source) length rather than an equal split - a
        // 5s jingle shouldn't eat as much room as a 3min song just because both got added.
        redistributeAudioTracks(track, durationMs) {
            const clips = track.clips
            if (!clips.length || durationMs <= 0) return

            const weights = clips.map(c => Math.max(1, c.sourceDurationMs || (c.timelineEndMs - c.timelineStartMs) || 1))
            const totalWeight = weights.reduce((sum, w) => sum + w, 0)

            let cursorMs = 0
            clips.forEach((clip, i) => {
                const shareMs = (weights[i] / totalWeight) * durationMs
                clip.timelineStartMs = cursorMs
                clip.timelineEndMs = cursorMs + shareMs
                clip.trimStartMs = 0
                clip.trimEndMs = clip.sourceDurationMs ? Math.min(shareMs, clip.sourceDurationMs) : shareMs
                cursorMs += shareMs
            })
        },

        readAudioDuration(url) {
            return new Promise((resolve) => {
                const probe = new Audio()
                probe.preload = 'metadata'
                probe.onloadedmetadata = () => resolve(Math.round(probe.duration * 1000))
                probe.onerror = () => resolve(0)
                probe.src = url
            })
        },

        removeAudioClip(clipId) {
            const track = getAudioTrack(this.timeline)
            track.clips = track.clips.filter(c => c.id !== clipId)
            this.markDirty()
            this.refreshPreview()
        },

        onUpdateAudioClip({ clipId, patch }) {
            const track = getAudioTrack(this.timeline)
            const clip = track.clips.find(c => c.id === clipId)
            if (!clip) return
            Object.assign(clip, patch)

            // trimStartMs/trimEndMs (what actually gets played from the source, see audioMixer.js
            // and TimelinePlayer's syncBackgroundAudio) aren't part of this patch - without this,
            // dragging the timeline window shorter/longer wouldn't change what's actually audible.
            if (patch.timelineStartMs !== undefined || patch.timelineEndMs !== undefined) {
                const windowMs = clip.timelineEndMs - clip.timelineStartMs
                clip.trimEndMs = clip.trimStartMs + (clip.sourceDurationMs ? Math.min(windowMs, clip.sourceDurationMs - clip.trimStartMs) : windowMs)
            }

            this.markDirty()
            this.refreshPreview()
        },

        // Opening the silence tool always starts (or resumes) a review rather than committing
        // anything directly - re-opening it on a clip that was already processed loads its
        // *current* segments back into the preview (so "modifier les curseurs"/re-editing an
        // earlier pass works the same way as reviewing a fresh detection), rather than always
        // requiring a brand new detection pass. A clip that's never been touched starts from a
        // single full segment, same as its own real (uncut) state.
        initSilencePreview() {
            const clip = this.selectedClip
            if (!clip || (this.silencePreview && this.silencePreview.clipId === clip.id)) return
            this.silencePreview = {
                clipId: clip.id,
                segments: (clip.segments || []).map(s => ({ ...s }))
            }
        },

        async runSilenceDetection() {
            if (!this.selectedClip || this.detectingSilence) return
            this.detectingSilence = true
            const clipId = this.selectedClip.id
            try {
                const source = this.resolveSource(this.selectedClip.fileId)
                // The decode (analyzeAudioLoudness) is the expensive part - cached per clip so the
                // sensitivity/minimum-duration sliders can re-run detection instantly afterwards
                // (updateSilenceSettings below) without re-fetching/re-decoding the audio track.
                const profile = await analyzeAudioLoudness(source.url)
                this.$set(this.silenceAnalysisCache, clipId, profile)
                this.recomputeSilencePreview(clipId)
            } finally {
                this.detectingSilence = false
            }
        },

        // Cheap re-extraction from the already-decoded loudness profile into the *preview* (not
        // committed) - used both right after a fresh detection and whenever the sensitivity/
        // minimum-duration sliders change, so tuning them doesn't need a full re-decode each time.
        recomputeSilencePreview(clipId) {
            const clip = this.mediaTrack.clips.find(c => c.id === clipId)
            const profile = this.silenceAnalysisCache[clipId]
            if (!clip || !profile) return

            const ranges = extractSilenceRanges(profile, {
                thresholdRatio: this.silenceThresholdRatio,
                minSilenceDurationMs: this.silenceMinDurationMs
            })
            this.silencePreview = { clipId, segments: computeSegmentsFromSilenceRanges(clip, ranges) }
            // Detecting zero ranges (genuinely no silent passage in this clip) looked identical to
            // the feature just not doing anything - nothing visibly changed either way. Surfacing
            // the actual count removes that ambiguity.
            this.silenceDetectionResult = { clipId, count: ranges.length }
        },

        // The sliders now show throughout the whole review (see silencePreviewActive), including
        // right after *re-opening* an already-processed clip - which restores its segments into
        // the preview (initSilencePreview) but has no decoded loudness profile to re-tune from in
        // *this* session (the cache is only ever populated by an actual detection pass). Without
        // this fallback, moving either slider silently did nothing at all in that case - it looked
        // broken because there was nothing left to recompute from. Running a fresh detection here
        // transparently fixes that: it decodes+caches the profile, and (since it goes through
        // recomputeSilencePreview internally) immediately re-extracts using the slider's own
        // just-changed value.
        async updateSilenceSettings(patch) {
            if (patch.thresholdRatio !== undefined) this.silenceThresholdRatio = patch.thresholdRatio
            if (patch.minSilenceDurationMs !== undefined) this.silenceMinDurationMs = patch.minSilenceDurationMs
            if (!this.selectedClip) return
            if (this.silenceAnalysisCache[this.selectedClip.id]) {
                this.recomputeSilencePreview(this.selectedClip.id)
            } else {
                await this.runSilenceDetection()
            }
        },

        // Include/exclude a portion directly on the timeline while reviewing (see
        // TimelineClip.vue's previewSegments/onZoneClick) - purely local to the preview, nothing
        // actually changes on the clip (or reflows the rest of the timeline) until validated.
        toggleSilencePreviewSegment({ segmentIndex, enabled }) {
            if (!this.silencePreview || !this.silencePreview.segments[segmentIndex]) return
            this.silencePreview.segments[segmentIndex].enabled = enabled
        },

        // Dragging a boundary between two adjacent segments (see TimelineClip.vue's
        // onBoundaryMouseDown) retimes the cut without changing what's kept vs excluded either
        // side of it.
        moveSilencePreviewBoundary({ boundaryIndex, atMs }) {
            if (!this.silencePreview) return
            const segments = this.silencePreview.segments
            const before = segments[boundaryIndex]
            const after = segments[boundaryIndex + 1]
            if (!before || !after) return
            before.endMs = atMs
            after.startMs = atMs
        },

        // Resets the *preview* back to "nothing cut" - still requires validating like any other
        // review change, so the user sees the restored, uncut clip before committing to it.
        resetSilencePreview() {
            if (!this.silencePreview) return
            const clip = this.mediaTrack.clips.find(c => c.id === this.silencePreview.clipId)
            if (!clip) return
            const totalMs = clip.trimEndMs - clip.trimStartMs
            this.silencePreview.segments = [{ startMs: 0, endMs: totalMs, enabled: true }]
        },

        // Commits whatever the review currently shows - this is the only place a silence pass
        // actually changes the clip (and, via recomputeTimelineLayout, ripples into every clip
        // after it) rather than the constantly-live-editing behavior this used to have.
        validateSilencePreview() {
            if (!this.silencePreview) return
            const clip = this.mediaTrack.clips.find(c => c.id === this.silencePreview.clipId)
            if (clip) {
                clip.segments = this.silencePreview.segments.map(s => ({ ...s }))
                clip.silenceRanges = clip.segments
                    .filter(s => !s.enabled)
                    .map(s => ({ startMs: s.startMs + clip.trimStartMs, endMs: s.endMs + clip.trimStartMs, confidence: 1 }))
                recomputeTimelineLayout(this.timeline)
                this.markDirty()
                this.refreshPreview()
            }
            this.silencePreview = null
            this.activeClipTool = null
        },

        // Discards the review entirely, leaving the clip exactly as it was before opening the tool.
        cancelSilencePreview() {
            this.silencePreview = null
            this.activeClipTool = null
        },

        // documentMode isn't part of the timeline (see toggleDocumentMode), so compared on its own
        hasChangesSinceOpen() {
            return JSON.stringify(this.timeline) !== this.initialTimelineJson || this.documentMode !== this.initialDocumentMode
        },

        async finish() {
            if (this.exporting || !this.timeline) return

            // photo export uses the current frame, record it so a moved playhead counts as a change
            if (this.isPhotoMode) this.timeline.photoFrameMs = this.currentMs
            // Nothing changed since open: keep the previous value (original file, or the earlier
            // export on a re-edit) instead of re-encoding - same path as closing without edits
            if (!this.hasChangesSinceOpen()) {
                this.$emit('clickOutside')
                return
            }

            this.exporting = true

            // Extra clips/audio added on top of the origin video only ever exist as in-memory blobs
            // in this component - they must travel back up so a future re-edit (or a retry after a
            // failure below) can resolve every clip referenced by the saved timelineState (see
            // tryRestoreTimeline). Computed once up front, before export-start even, so re-editing
            // while THIS export is still running (see StandardTab.vue's startEditVideoOrigin) can resume
            // from the edit that's actually being encoded right now - not whatever was there before
            // this "Terminer" click, which is all export-start used to carry.
            const extraFiles = {}
            Object.keys(this.fileRegistry).forEach((fileId) => {
                if (fileId === ORIGIN_FILE_ID) return
                const entry = this.fileRegistry[fileId]
                if (entry.localFile) extraFiles[fileId] = entry.localFile
            })
            const timelineState = cloneTimeline(this.timeline)

            // One-off still of exactly what the popup's own preview shows right now (current
            // frame, with any crop/overlays already applied) - grabbed while it's still mounted and
            // normally rendered, i.e. strictly before the line below hides it. Used as the
            // export-in-progress placeholder (see the portal below); null (getSnapshotDataUrl's own
            // failure case, or isPhotoMode has no VideoPreviewCanvas ref at all) just falls back to
            // the plain origin media there.
            // Closing the crop tool first: while it's open the preview deliberately shows the full
            // uncropped source (and, in Remplir, in a source-shaped frame), which is exactly what
            // this thumbnail must not be. It's open by default (see created), so this is the normal
            // path - captureSnapshot waits for the resulting redraw before reading the canvas.
            this.activeClipTool = null
            try {
                this.exportSnapshotUrl = this.$refs.preview ? await this.$refs.preview.captureSnapshot() : null
            } catch (e) {
                console.error('[MediaEditor] finish: snapshot capture failed', e)
                this.exportSnapshotUrl = null
            }

            // Closes the popup from the user's perspective right away instead of blocking on the
            // export - see the template's root v-if. Nothing below this point depends on any
            // $refs into the (now unmounted) live preview/timeline UI, only on this.timeline/
            // this.fileRegistry, so hiding it doesn't interrupt the export itself.
            this.backgroundExport = true

            // The actual encode is owned by the mediaExportJobs store, not this component's own
            // promise chain - a store action keeps running even if MediaEditor.vue/StandardTab.vue
            // get destroyed by a route navigation away from postCreator (postCreator has no
            // <keep-alive> - see StandardTab.vue's registerMediaEditorSession). jobId travels
            // alongside export-start so a submit-time listener (see PostCreator.vue's
            // sendPostAllPost) can await this exact job from the store directly, independent of
            // whether this component instance still exists by the time it resolves.
            const jobId = await this.$store.dispatch('mediaExportJobs/startExport', {
                scopeKey: this.scopeKey,
                kind: this.isPhotoMode ? 'photo' : 'video',
                timeline: this.timeline,
                originUrl: this.videoSrc,
                originKind: this.originSourceType,
                extraFiles,
                atMs: this.currentMs
            }).catch((e) => { console.error('[MediaEditor] finish: startExport dispatch failed', e); return null })
            this.$emit('export-start', { timelineState, extraFiles, jobId })

            if (!jobId) {
                this.exporting = false
                const error = this.$t('dash.post-creator.media-editor.editor.export-error')
                this.$emit('export-failed', { error, timelineState, extraFiles, jobId: null })
                return
            }

            const unwatch = this.$store.watch(
                (state) => state.mediaExportJobs.jobs[jobId],
                (job) => {
                    if (!job) return
                    if (job.status === 'running') {
                        this.exportProgressPct = job.progress
                        this.$emit('export-progress', job.progress)
                        return
                    }
                    unwatch()
                    this.exporting = false
                    if (job.status === 'done') {
                        this.$emit('finished', { file: job.resultFile, timelineState, extraFiles, jobId })
                    } else {
                        // The popup is already gone by this point (backgroundExport, above) - the
                        // error and the edits that led to it need to travel back up to Form.vue
                        // instead of just sitting unseen in this now-hidden component's own state.
                        const error = this.$t('dash.post-creator.media-editor.editor.export-error')
                        this.$emit('export-failed', { error, timelineState, extraFiles, jobId })
                    }
                },
                { immediate: true }
            )
        }
    }
}
</script>

<style lang="scss" scoped></style>
